import { Fragment, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import InputBase from '@mui/material/InputBase';
import { useAppSelector } from '../../app/hooks';
import { useOptionList } from '../../features/options/useOptions';
import { selectAuthUser } from '../../features/auth/authSlice';
import {
  useUsersQuery, useCreateUserMutation, useUpdateUserMutation,
  useToggleUserMutation, useDeleteUserMutation, useResetUserPasswordMutation,
} from '../../services/api';
import { CH, CHMAP, CHCOLOR } from '../../constants/channels';
import { ROLE_DESC } from '../../data/catalog';
import TableCard from '../../components/common/TableCard';
import AppButton from '../../components/common/AppButton';
import Pagination from '../../components/common/Pagination';
import QueryState from '../../components/common/QueryState';
import { TableSkeleton } from '../../components/common/Shimmer';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useToast } from '../../components/common/Toast';
import { ReadOnlyNotice } from '../../components/common/Access';
import { usePermissions } from '../../features/auth/permissions';
import ResetPasswordDialog from './ResetPasswordDialog';
import { TextField, SelectField, FieldLabel, PillSelect, CONTROL_SX } from '../../components/common/FormField';
import { ChannelChip, TagChip, StatusDot, FootNote } from '../../components/common/Bits';

const LIMIT = 12;
/** Matches the server's floor, so a short one never leaves the browser. */
const MIN_PASSWORD = 6;

function ChannelCheck({ label, color, checked, disabled, onChange }) {
  return (
    <Box
      component="label"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        p: '7px 11px',
        border: '1px solid',
        borderColor: checked && !disabled ? 'var(--mint)' : 'var(--border)',
        borderRadius: '999px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 12.5,
        background: checked && !disabled ? '#eaf7f0' : 'var(--card-2)',
        opacity: disabled ? 0.6 : 1,
      }}
    >
      <Checkbox
        size="small"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        sx={{ p: 0, color: 'var(--side)', '&.Mui-checked': { color: 'var(--side)' } }}
      />
      {color && <Box component="span" sx={{ width: 9, height: 9, borderRadius: '50%', background: color }} />}
      {label}
    </Box>
  );
}

function UserForm({ user, roles, saving, onCancel, onSave }) {
  const toast = useToast();
  // every key present from the first render, or the password input would start
  // uncontrolled (value=undefined) and React would warn when the effect fills it in
  const [form, setForm] = useState({ name: '', email: '', role: 'Analyst', channels: [], password: '' });
  const [all, setAll] = useState(false);

  /**
   * Admin is never on offer. There is one Admin account by design — a second one
   * could remove the first, and then nobody can manage the team at all.
   *
   * The one exception is editing the Admin themselves: their existing role has to
   * stay in the list, or the field would render blank and demote them on save.
   */
  const selectableRoles = roles.filter((r) => r !== 'Admin' || user?.role === 'Admin');

  useEffect(() => {
    const u = user || { name: '', email: '', role: 'Analyst', channels: [] };
    setAll(u.channels === 'all');
    // password is only part of creating someone — an existing account's is changed
    // with the 🔑 button, so it is never pre-filled here
    setForm({ name: u.name, email: u.email, role: u.role, channels: u.channels === 'all' ? [] : u.channels, password: '' });
  }, [user]);

  const toggleChannel = (id) =>
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(id) ? f.channels.filter((c) => c !== id) : [...f.channels, id],
    }));

  const submit = () => {
    const name = form.name.trim();
    const email = form.email.trim();
    if (!name) return toast('Name is required', 'error');
    if (!email) return toast('Email is required', 'error');

    const data = { name, email, role: form.role, channels: all ? 'all' : form.channels };

    // only on create — the server enforces the same six-character floor
    if (!user) {
      const password = form.password.trim();
      if (!password) return toast('Password is required', 'error');
      if (password.length < MIN_PASSWORD) {
        return toast(`Password must be at least ${MIN_PASSWORD} characters`, 'error');
      }
      data.password = password;
    }

    return onSave(data);
  };

  return (
    <Paper
      sx={{
        background: 'var(--card)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow)',
        p: '18px',
        mb: 2,
      }}
    >
      <Box sx={{ m: '0 0 14px', fontFamily: 'var(--disp)', fontSize: 15, fontWeight: 600 }}>
        {user ? 'Edit user' : 'Add user'}
      </Box>
      <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField label="Full name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Anita Sharma" />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <TextField label="Email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="name@trustlab.co.in" />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <SelectField label="Role" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} options={selectableRoles} />
        </Grid>
      </Grid>

      {/* creating only — an existing account's password is changed with the 🔑 button */}
      {!user && (
        <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Password"
              required
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder={`At least ${MIN_PASSWORD} characters`}
              inputProps={{ style: { fontFamily: 'var(--mono)' } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 8 }}>
            <FieldLabel>&nbsp;</FieldLabel>
            <Box sx={{ fontSize: 12, color: 'var(--muted)', pt: '10px' }}>
              They sign in with this. Shown in clear so you can read it out — ask them to change it afterwards.
            </Box>
          </Grid>
        </Grid>
      )}

      <FieldLabel>Channel access</FieldLabel>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        <ChannelCheck label="All channels" checked={all} onChange={(e) => setAll(e.target.checked)} />
        {CH.map((c) => (
          <ChannelCheck
            key={c.id}
            label={c.name}
            color={CHCOLOR[c.id]}
            checked={!all && form.channels.includes(c.id)}
            disabled={all}
            onChange={() => toggleChannel(c.id)}
          />
        ))}
      </Box>


      <Box sx={{ display: 'flex', gap: 1.25, justifyContent: 'flex-end', mt: 2 }}>
        <AppButton onClick={onCancel} disabled={saving}>Cancel</AppButton>
        <AppButton tone="primary" onClick={submit} disabled={saving}>
          {saving ? 'Saving…' : 'Save user'}
        </AppButton>
      </Box>
    </Paper>
  );
}

export default function UsersPage() {
  const toast = useToast();
  const roles = useOptionList('roles');
  const me = useAppSelector(selectAuthUser);
  // `role` below is this page's filter; the signed-in user's own role needs another name
  const { canManageTeam, role: myRole, visibleChannels } = usePermissions();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [term, setTerm] = useState('');
  const [role, setRole] = useState('');
  const [status, setStatus] = useState('');
  const [channel, setChannel] = useState('');
  const [page, setPage] = useState(1);
  const [confirm, setConfirm] = useState(null);
  const [resetting, setResetting] = useState(null);

  // Debounced so typing a name is one request, not one per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setTerm(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [role, status, channel]);

  const listArgs = useMemo(
    () => ({
      search: term || undefined,
      role: role || undefined,
      active: status || undefined,
      channel: channel || undefined,
      page,
      limit: LIMIT,
    }),
    [term, role, status, channel, page]
  );

  const listQuery = useUsersQuery(listArgs);
  const [createUser, createState] = useCreateUserMutation();
  const [updateUser, updateState] = useUpdateUserMutation();
  const [toggleUser] = useToggleUserMutation();
  const [deleteUser] = useDeleteUserMutation();
  const [resetPassword, resetState] = useResetUserPasswordMutation();
  const saving = createState.isLoading || updateState.isLoading;

  const fail = (e, fallback) => toast(e?.data?.error || e?.message || fallback, 'error');

  const closeForm = () => {
    setEditing(null);
    setFormOpen(false);
  };

  const save = async (data) => {
    try {
      if (editing) {
        await updateUser({ id: editing.id, ...data }).unwrap();
        toast('User updated');
      } else {
        await createUser(data).unwrap();
        toast('User added');
      }
      closeForm();
    } catch (e) {
      fail(e, "Couldn't save that user");
    }
  };

  const flip = async (u) => {
    try {
      await toggleUser(u.id).unwrap();
      toast(u.active ? `${u.name} disabled` : `${u.name} enabled`);
    } catch (e) {
      fail(e, "Couldn't change that user");
    }
  };

  const setNewPassword = async (password) => {
    try {
      await resetPassword({ id: resetting.id, password }).unwrap();
      toast(`Password set for ${resetting.name}`);
      setResetting(null);
    } catch (e) {
      fail(e, "Couldn't set that password");
    }
  };

  const runConfirm = async () => {
    const c = confirm;
    setConfirm(null);
    try {
      await deleteUser(c.id).unwrap();
      toast('User removed');
    } catch (e) {
      fail(e, "Couldn't remove that user");
    }
  };

  return (
    <>
      {!canManageTeam && <ReadOnlyNotice role={myRole} what="the team" />}
      {formOpen && canManageTeam && <UserForm user={editing} roles={roles} saving={saving} onCancel={closeForm} onSave={save} />}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, mb: 1.5, flexWrap: 'wrap' }}>
        <InputBase
          sx={{ ...CONTROL_SX, width: { xs: '100%', sm: 240 } }}
          placeholder="Search name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <PillSelect
          value={role}
          onChange={(e) => setRole(e.target.value)}
          options={[{ value: '', label: 'All roles' }, ...roles]}
          sx={{ minWidth: 140 }}
        />
        <PillSelect
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          options={[
            { value: '', label: 'Any status' },
            { value: 'true', label: 'Active only' },
            { value: 'false', label: 'Disabled only' },
          ]}
          sx={{ minWidth: 140 }}
        />
        <PillSelect
          value={channel}
          onChange={(e) => setChannel(e.target.value)}
          // an "all channels" teammate matches every one of these, which is the point
          options={[{ value: '', label: 'Any channel' }, ...visibleChannels.map((c) => ({ value: c.id, label: c.name }))]}
          sx={{ minWidth: 150 }}
        />
      </Box>

      <QueryState query={listQuery} skeleton={<TableSkeleton columns={6} rows={6} />}>
        {(data) => (
          <>
            <TableCard
              title={`${data.total} team member${data.total === 1 ? '' : 's'}${term || role || status ? ' matching' : ''}`}
              actions={
                canManageTeam ? (
                  <AppButton tone="gold" onClick={() => { setEditing(null); setFormOpen(true); }}>
                    ＋ Add User
                  </AppButton>
                ) : null
              }
            >
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Channel Access</th>
                    <th>Status</th>
                    {canManageTeam && <th style={{ textAlign: 'right' }}>Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ color: 'var(--muted)', textAlign: 'center', padding: '18px 0' }}>
                        No team members match those filters.
                      </td>
                    </tr>
                  )}
                  {data.items.map((u) => {
                    // the server refuses either action on your own account; don't offer it
                    const isSelf = u.id === me?.id;
                    return (
                      <tr key={u.id}>
                        <td>
                          <b>{u.name}</b>
                          {isSelf && <Box component="span" sx={{ color: 'var(--muted)', fontSize: 11.5 }}> · you</Box>}
                        </td>
                        <td style={{ color: 'var(--ink-2)' }}>{u.email}</td>
                        <td><TagChip title={ROLE_DESC[u.role] || ''}>{u.role}</TagChip></td>
                        <td>
                          {u.channels === 'all' ? (
                            <TagChip>All channels</TagChip>
                          ) : u.channels.length ? (
                            u.channels.map((id, i) => (
                              // plain inline chips separated by a real space — a flex wrapper would
                              // blockify them and stack each chip on its own line
                              <Fragment key={id}>
                                {i > 0 && ' '}
                                <ChannelChip small color={CHCOLOR[id]}>
                                  {CHMAP[id]?.name || id}
                                </ChannelChip>
                              </Fragment>
                            ))
                          ) : (
                            '—'
                          )}
                        </td>
                        <td><StatusDot on={u.active} /></td>
                        {canManageTeam && (
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <AppButton mini onClick={() => { setEditing(u); setFormOpen(true); }}>Edit</AppButton>{' '}
                            <AppButton mini onClick={() => setResetting(u)} title="Set a new password">🔑</AppButton>{' '}
                            <AppButton mini disabled={isSelf} onClick={() => flip(u)}>
                              {u.active ? 'Disable' : 'Enable'}
                            </AppButton>{' '}
                            <AppButton
                              mini
                              tone="danger"
                              disabled={isSelf}
                              onClick={() => setConfirm({ id: u.id, name: u.name })}
                            >
                              Remove
                            </AppButton>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableCard>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
              <Pagination page={data.page} pages={data.pages} onChange={setPage} />
            </Box>
          </>
        )}
      </QueryState>

      <FootNote>
        Roles: {roles.map((r, i) => (
          <span key={r}>
            {i > 0 && ' · '}
            <b>{r}</b> — {ROLE_DESC[r] || 'custom role'}
          </span>
        ))}
        . Only Admins can change the team.
      </FootNote>

      <ResetPasswordDialog
        open={!!resetting}
        user={resetting}
        saving={resetState.isLoading}
        onCancel={() => setResetting(null)}
        onConfirm={setNewPassword}
      />

      <ConfirmDialog
        open={!!confirm}
        tone="danger"
        title="Remove user"
        message={confirm ? `Remove “${confirm.name}” from the team?` : ''}
        detail="They lose access immediately. This cannot be undone."
        confirmLabel="Remove"
        onCancel={() => setConfirm(null)}
        onConfirm={runConfirm}
      />
    </>
  );
}
